import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { type MessageKey } from '@/i18n'
import { BilingualLabel } from '@/ui'
import { ScreenTitle } from '@/features/phone/components/ScreenTitle'

/** Screen frame for gate flows: the map-style title row (back, title, optional line), then the flow. */
export function GateScreen({
  title,
  eyebrow,
  back = '/m/gate',
  aside,
  children,
}: {
  title: MessageKey
  eyebrow?: string
  /** Path, a handler, or null for no back link. */
  back?: string | (() => void) | null
  aside?: ReactNode
  children: ReactNode
}) {
  const navigate = useNavigate()
  return (
    <div className="flex flex-col gap-4">
      <ScreenTitle
        title={title}
        sub={eyebrow}
        aside={aside}
        onBack={
          back === null ? undefined : () => (typeof back === 'function' ? back() : navigate(back))
        }
      />
      {children}
    </div>
  )
}

/** Bilingual text sized for a button: Hindi line, English under it, normal case (360 px safe). */
export function BiText({ k }: { k: MessageKey }) {
  return (
    <BilingualLabel
      k={k}
      layout="stacked"
      className="items-center text-center font-sans text-[15px] normal-case leading-tight tracking-normal"
      secondaryClassName="text-[11px]"
    />
  )
}

/** Sticky bottom action bar above the phone nav: the step's main buttons. */
export function ActionBar({ children }: { children: ReactNode }) {
  return (
    <div className="sticky bottom-20 z-20 -mx-4 mt-2 flex flex-col gap-2 border-t border-rule bg-paper/95 px-4 py-3 backdrop-blur">
      {children}
    </div>
  )
}

/** A big figure the farmer can read from across the table. */
export function BigFigure({
  label,
  value,
  tone,
}: {
  label: ReactNode
  value: ReactNode
  tone?: 'ok' | 'warn'
}) {
  return (
    <div className="rounded-card border border-rule bg-surface px-3 py-2.5">
      <div className="text-[12px] text-muted">{label}</div>
      <div
        className={
          'font-mono text-[26px] font-medium tabular leading-tight ' +
          (tone === 'warn' ? 'text-warn' : tone === 'ok' ? 'text-ok' : 'text-ink')
        }
      >
        {value}
      </div>
    </div>
  )
}
